/**
 * Auto-order coverage for the family weekly order surface.
 *
 * Answers "which of this location's kids will have meals placed
 * automatically?" so the week view can nudge accordingly: nobody covered →
 * the set-up reminder card; everybody covered → the compact "auto-order is
 * on" card; a mix → a by-name warning that the uncovered kids could miss
 * order days, plus per-kid copy inside the day cards.
 *
 * A child counts as covered only when they have an ENABLED (not paused /
 * initialized / cancelled) subscription at THIS school — a paused
 * subscription places no orders, so for "will meals show up?" purposes it
 * reads as not covered. A child can hold one subscription per mealtime
 * (breakfast AND lunch), so the row that speaks for them here is their
 * best-ranked one: ENABLED beats paused/initialized beats cancelled.
 */

import { useMemo } from 'react'

import { type FamilySubscriptionWire, useFamilySubscriptionsQuery } from '@/api/family-subscriptions'
import type { OrderingStudent } from '@/api/orders-family'

export interface AutoOrderCoverage {
  /** none = no kid covered · all = every kid covered · mixed = some but not all. */
  state: 'none' | 'all' | 'mixed'
  /** userIds of children with an ENABLED subscription at this school. */
  activeUserIds: ReadonlySet<string>
  covered: OrderingStudent[]
  uncovered: OrderingStudent[]
  /** Best (non-archived, see `coverageRank`) subscription per child userId. */
  subByUserId: ReadonlyMap<string, FamilySubscriptionWire>
}

/**
 * Which of a child's subscriptions speaks for them on this surface. They can
 * hold one per mealtime, so "will meals show up?" must answer yes when ANY of
 * them is ENABLED rather than whichever row the list happened to return first.
 */
function coverageRank(sub: FamilySubscriptionWire): number {
  if (sub.status === 'ENABLED') {
    return 2
  }
  return sub.status === 'CANCELLED' ? 0 : 1
}

/**
 * Pure coverage computation — exported for tests. `schoolId` scopes the
 * check to the location being viewed (subscriptions carry the campus they
 * order at; a sibling's subscription at another campus doesn't cover them
 * here).
 */
export function computeAutoOrderCoverage(
  children: readonly OrderingStudent[],
  subscriptions: readonly FamilySubscriptionWire[],
  schoolId: string,
): AutoOrderCoverage {
  const subByUserId = new Map<string, FamilySubscriptionWire>()
  for (const sub of subscriptions) {
    if (sub.isArchived || sub.schoolId !== schoolId) {
      continue
    }
    const existing = subByUserId.get(sub.studentUserId)
    if (!existing || coverageRank(sub) > coverageRank(existing)) {
      subByUserId.set(sub.studentUserId, sub)
    }
  }

  const activeUserIds = new Set<string>()
  const covered: OrderingStudent[] = []
  const uncovered: OrderingStudent[] = []
  for (const child of children) {
    if (subByUserId.get(child.userId)?.status === 'ENABLED') {
      activeUserIds.add(child.userId)
      covered.push(child)
    } else {
      uncovered.push(child)
    }
  }

  const state = covered.length === 0 ? 'none' : uncovered.length === 0 ? 'all' : 'mixed'
  return { state, activeUserIds, covered, uncovered, subByUserId }
}

/** "Alex" · "Alex and Sam" · "Alex, Sam, and Riley". */
export function formatNameList(names: readonly string[]): string {
  if (names.length <= 1) {
    return names[0] ?? ''
  }
  if (names.length === 2) {
    return `${names[0]} and ${names[1]}`
  }
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`
}

/**
 * The href a "turn it on / manage" CTA should target for the given children:
 * a single child goes straight to their existing subscription (manage) or
 * the pre-seeded wizard; multiple children land on the wizard's student-picker
 * step (Sheila 7/21 QA, item 8) — "Set up auto-order" must open the auto-order
 * flow directly, not the My Family page, where the parent then hunts for the
 * right kid's row.
 */
export function autoOrderTargetHref(
  children: readonly OrderingStudent[],
  subByUserId: ReadonlyMap<string, FamilySubscriptionWire>,
): string {
  if (children.length === 1) {
    const child = children[0]!
    const sub = subByUserId.get(child.userId)
    if (sub && sub.status !== 'CANCELLED') {
      return `/family/auto-orders/${encodeURIComponent(sub.id)}`
    }
    return `/family/auto-orders/new?studentUserId=${encodeURIComponent(child.userId)}`
  }
  return '/family/auto-orders/new'
}

/**
 * Coverage for the given location's children, or null while the
 * subscriptions list is still loading (render nothing — no flash).
 */
export function useAutoOrderCoverage(
  children: readonly OrderingStudent[],
  schoolId: string | null,
): AutoOrderCoverage | null {
  const query = useFamilySubscriptionsQuery()
  return useMemo(() => {
    if (!query.data || !schoolId) {
      return null
    }
    return computeAutoOrderCoverage(children, query.data.subscriptions, schoolId)
  }, [query.data, children, schoolId])
}
